/**
 * Tab "Salas" del CenterDrawer — gestiona las rooms del centro abierto en el
 * drawer. Lógica portada desde la antigua `RoomsPage` (eliminada) y scoped al
 * centro en edición (no necesita `useActiveCenter` ni selector global).
 *
 * Acceso: superadmin + center_admin (el backend de `/api/admin/rooms` ya
 * acepta ambos roles con filtrado por centro vía `requireCenter()`).
 */
import { useState, useMemo } from 'react';

import { useRooms, useCreateRoom, useUpdateRoom, useDeleteRoom } from '@hooks/useAdminResources';
import { useConfirm } from '@hooks/useConfirm';
import { ApiRequestError } from '@api/client';
import type { Room } from '@api/types';
import * as Icons from '@components/Icons';
import { RowMenu } from '@components/admin/RowMenu';

interface RoomModalProps {
  room: Room | null;
  onClose: () => void;
  onSave: (data: { name: string; isActive?: boolean; sortOrder?: number }) => Promise<void>;
  isSaving: boolean;
}

function RoomModal({ room, onClose, onSave, isSaving }: RoomModalProps) {
  const [name, setName] = useState(room?.name ?? '');
  const [isActive, setIsActive] = useState(room?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState<number>(room?.sortOrder ?? 0);
  const [err, setErr] = useState('');

  const isEdit = room !== null;

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setErr('');
    if (!name.trim()) { setErr('El nombre es obligatorio.'); return; }
    if (!Number.isInteger(sortOrder) || sortOrder < 0) {
      setErr('El orden debe ser un entero ≥ 0.');
      return;
    }
    try {
      await onSave({ name: name.trim(), isActive, sortOrder });
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 409) {
        setErr('Ya existe una sala con ese nombre en este centro.');
      } else {
        setErr('Error al guardar. Inténtalo de nuevo.');
      }
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => { e.stopPropagation(); }}>
        <div className="modal-head">
          <div className="modal-title">{isEdit ? `Editar sala · ${room.name}` : 'Nueva sala'}</div>
          <button className="icon-btn" data-variant="ghost" onClick={onClose}><Icons.X size={14} /></button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          {err && (
            <div style={{ padding: '10px 14px', background: 'var(--danger-soft)', border: '1px solid oklch(0.85 0.05 25)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--accent-dark)', marginBottom: 12 }}>
              {err}
            </div>
          )}
          <div className="field">
            <label className="field-label">Nombre <span style={{ color: 'var(--accent)' }}>*</span></label>
            <input className="input" value={name} onChange={(e) => { setName(e.target.value); }} autoFocus />
          </div>
          {isEdit && (
            <>
              <div className="field">
                <label className="field-label">Orden de asignación</label>
                <input
                  className="input mono"
                  type="number"
                  min={0}
                  step={1}
                  value={sortOrder}
                  onChange={(e) => { setSortOrder(parseInt(e.target.value, 10) || 0); }}
                  style={{ width: 120 }}
                />
                <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 4 }}>
                  Menor primero. Define el orden de la asignación automática (first-fit) cuando varias salas son candidatas.
                </p>
              </div>
              <div className="switch-row">
                <div className="switch-row-info">
                  <div className="switch-row-name">Sala activa</div>
                  <div className="switch-row-desc">Las salas inactivas no están disponibles para reservas.</div>
                </div>
                <div className="switch" data-on={isActive} onClick={() => { setIsActive((v) => !v); }} />
              </div>
            </>
          )}
        </form>
        <div className="modal-foot">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn" data-variant="primary" disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear sala'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface CenterDrawerSalasTabProps {
  centerId: string;
}

export function CenterDrawerSalasTab({ centerId }: CenterDrawerSalasTabProps) {
  const confirm = useConfirm();

  const { data: roomList = [], isLoading } = useRooms(centerId);
  const createMutation = useCreateRoom(centerId);
  const updateMutation = useUpdateRoom(centerId);
  const deleteMutation = useDeleteRoom(centerId);

  const [q, setQ] = useState('');
  const [modalRoom, setModalRoom] = useState<Room | null | undefined>(undefined);

  const sortedRoomList = useMemo(() => {
    return [...roomList].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.id.localeCompare(b.id);
    });
  }, [roomList]);

  const filtered = useMemo(() => {
    if (!q) return sortedRoomList;
    const s = q.toLowerCase();
    return sortedRoomList.filter((r) => r.name.toLowerCase().includes(s));
  }, [sortedRoomList, q]);

  const activeCount = roomList.filter((r) => r.isActive).length;

  async function handleSave(data: { name: string; isActive?: boolean; sortOrder?: number }) {
    if (modalRoom === null) {
      await createMutation.mutateAsync({ name: data.name });
    } else if (modalRoom) {
      await updateMutation.mutateAsync({ id: modalRoom.id, data });
    }
    setModalRoom(undefined);
  }

  async function handleDelete(room: Room) {
    const ok = await confirm({
      title: 'Eliminar sala',
      message: `¿Eliminar la sala "${room.name}"?`,
      tone: 'danger',
      confirmLabel: 'Eliminar',
    });
    if (ok) {
      await deleteMutation.mutateAsync(room.id);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
          {roomList.length} salas · {activeCount} activas
        </div>
        <button
          className="btn"
          data-variant="primary"
          onClick={(e) => { e.preventDefault(); setModalRoom(null); }}
        >
          <Icons.Plus size={14} /> Nueva sala
        </button>
      </div>

      <div className="toolbar" style={{ marginBottom: 8 }}>
        <div className="search">
          <Icons.Search size={14} className="ico" />
          <input
            className="input"
            placeholder="Buscar sala…"
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
          />
        </div>
        <div className="toolbar-spacer" />
        <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-soft)' }}>
          {filtered.length} resultados
        </span>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Orden</th>
            <th>Sala</th>
            <th>Estado</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--fg-muted)' }}>Cargando…</td></tr>
          )}
          {!isLoading && filtered.length === 0 && (
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--fg-muted)' }}>Sin salas</td></tr>
          )}
          {filtered.map((room) => (
            <tr key={room.id} data-clickable="true" onClick={() => { setModalRoom(room); }}>
              <td>
                <span className="mono" style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  {room.sortOrder}
                </span>
              </td>
              <td>
                <span style={{ fontWeight: 500 }}>{room.name}</span>
              </td>
              <td>
                {room.isActive
                  ? <span className="pill" data-tone="ok">Activa</span>
                  : <span className="pill" data-tone="neutral">Inactiva</span>}
              </td>
              <td className="cell-narrow">
                <div className="row-actions">
                  <button
                    className="icon-btn"
                    data-variant="ghost"
                    onClick={(e) => { e.stopPropagation(); setModalRoom(room); }}
                  >
                    <Icons.Edit size={13} />
                  </button>
                  <RowMenu items={[
                    { icon: Icons.Edit, label: 'Editar', onClick: () => { setModalRoom(room); } },
                    null,
                    { icon: Icons.Trash, label: 'Eliminar sala', tone: 'danger', onClick: () => { void handleDelete(room); } },
                  ]} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {modalRoom !== undefined && (
        <RoomModal
          room={modalRoom}
          onClose={() => { setModalRoom(undefined); }}
          onSave={handleSave}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}
